// The television station: the feed app running in its own realm, the games
// it is given to show, the featured game's screenshot drawn into the window
// the app leaves for it, and the announcer's voice.

import { CAPTION_RATE, PICTURE, SHOT, TV_SERVICE, type FeedItem, type TvGuestLine } from "../../tv/protocol.ts";
import { Babble } from "../audio/babble.ts";
import type { AppInfo, DeviceInfo } from "../core/types.ts";
import { BTN, DeviceRealm, type RunningRealm } from "../emu/realm.ts";
import type { TvScreen } from "../stage/tv.ts";
import { lang } from "../ui/lang.ts";

/** Canvas pixels per logical pixel of the picture. */
export const TV_DENSITY = 2;
const MUTED_KEY = "pocket-studio:tv-muted";

export interface StationOptions {
  runtimeUrl: string;
  /** Where scripts/build-tv.ts output is served, for example "/tv/". */
  baseUrl: string;
  /** The viewer chose the featured game. */
  onOpen(app: string): void;
}

/**
 * What the station shows: other authors' published games on the shelf, the
 * newest first; then the members' games off the shelf (`aired`), in the
 * order the server drew them; then the viewer's own and the starters.
 */
export function feedOf(
  apps: readonly AppInfo[],
  devices: readonly DeviceInfo[],
  aired: readonly AppInfo[] = [],
): { items: FeedItem[]; shots: Map<string, string> } {
  const rank = { shared: 0, mine: 1, starter: 2 } as const;
  const shown = (app: AppInfo) => app.status === "published" && app.discoverable !== false && (app.devices.length > 0 || app.kind === "site");
  const sorted = apps.filter(shown).sort((a, b) => rank[a.origin] - rank[b.origin] || b.updatedAt - a.updatedAt);
  const off = aired.filter((app) => shown(app) && !sorted.some((entry) => entry.id === app.id));
  const shelf = [...sorted.filter((app) => app.origin === "shared"), ...off, ...sorted.filter((app) => app.origin !== "shared")];
  const shots = new Map<string, string>();
  for (const app of shelf) if (app.shot) shots.set(app.id, app.shot);
  return {
    shots,
    items: shelf.map((app) => ({
      id: app.id,
      title: app.title,
      author: app.author,
      tagline: app.tagline,
      // A game built elsewhere runs on the devices it has a package for.
      devices: devices
        .filter((device) => app.devices.includes(device.id) || app.packages.some((entry) => entry.target === device.id))
        .map((device) => device.name),
      deviceIds: devices
        .filter((device) => app.devices.includes(device.id) || app.packages.some((entry) => entry.target === device.id))
        .map((device) => device.id),
      colors: app.colors,
    })),
  };
}

export class Station {
  private realm: RunningRealm | null = null;
  private frame = document.createElement("canvas");
  private context: CanvasRenderingContext2D;
  private shots = new Map<string, string>();
  private picture: HTMLImageElement | null = null;
  private feed = "";
  private minute = "";
  private audible = false;
  private voice = new Babble();
  private line = "";
  /** The game on screen now. */
  featured: string | null = null;

  constructor(private screen: TvScreen, private options: StationOptions) {
    this.context = screen.canvas.getContext("2d")!;
    try {
      this.voice.muted = localStorage.getItem(MUTED_KEY) === "1";
    } catch { /* storage is a convenience */ }
  }

  async boot(): Promise<void> {
    this.realm = await DeviceRealm.boot({
      runtimeUrl: this.options.runtimeUrl,
      packageId: "dev.pocket-nexus.studio.tv",
      bundleUrl: `${this.options.baseUrl}studio-tv.js`,
      pakUrl: `${this.options.baseUrl}studio-tv.pak`,
      viewport: [PICTURE.w, PICTURE.h],
      density: TV_DENSITY,
      primary: this.frame,
      services: [TV_SERVICE],
      onLine: (line) => this.onLine(line),
    });
    if (this.feed) this.realm.send(JSON.parse(this.feed));
    this.minute = "";
  }

  setFeed(apps: readonly AppInfo[], devices: readonly DeviceInfo[], aired: readonly AppInfo[] = []): void {
    const { items, shots } = feedOf(apps, devices, aired);
    this.shots = shots;
    const line = JSON.stringify({ t: "feed", items, lang: lang() });
    if (line === this.feed) return;
    this.feed = line;
    this.realm?.send(JSON.parse(line));
  }

  private onLine(text: string): void {
    let line: TvGuestLine;
    try {
      line = JSON.parse(text) as TvGuestLine;
    } catch {
      return;
    }
    if (line.t === "show") {
      this.featured = line.id;
      this.screen.static();
      const url = this.shots.get(line.id);
      this.picture = null;
      if (url) {
        const image = new Image();
        image.onload = () => {
          if (this.featured === line.id) this.picture = image;
        };
        image.src = url;
      }
    } else if (line.t === "say") {
      this.line = line.text;
      if (this.audible) this.voice.say(line.text, CAPTION_RATE);
    } else if (line.t === "open") {
      this.options.onOpen(line.id);
    }
  }

  /** Step the feed app and redraw the picture; true when the picture changed. */
  advance(dt: number): boolean {
    const realm = this.realm;
    if (!realm) return false;
    const now = new Date();
    const minute = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    if (minute !== this.minute) {
      this.minute = minute;
      realm.send({ t: "clock", s: minute });
    }
    if (!realm.advance(dt)) return false;
    const g = this.context;
    g.drawImage(this.frame, 0, 0);
    if (this.picture) {
      // The screenshot keeps its shape inside the window, on black.
      const k = TV_DENSITY;
      const scale = Math.min(SHOT.w / this.picture.width, SHOT.h / this.picture.height);
      const w = this.picture.width * scale;
      const h = this.picture.height * scale;
      g.fillStyle = "#0b0814";
      g.fillRect(SHOT.x * k, SHOT.y * k, SHOT.w * k, SHOT.h * k);
      g.drawImage(this.picture, (SHOT.x + (SHOT.w - w) / 2) * k, (SHOT.y + (SHOT.h - h) / 2) * k, w * k, h * k);
    }
    this.screen.refresh();
    return true;
  }

  /** Change the game: -1 the one before, 1 the next. */
  turn(by: -1 | 1): void {
    this.tap(by < 0 ? BTN.LEFT : BTN.RIGHT);
  }

  /** Open the featured game. */
  choose(): void {
    this.tap(BTN.CIRCLE);
  }

  /** Press a button for exactly one frame of the feed app. */
  tap(bit: number): void {
    const realm = this.realm;
    if (!realm) return;
    realm.press(bit, true);
    realm.afterNextTick(() => realm.press(bit, false));
  }

  /** The voice is heard only while the viewer is at the television. */
  setAudible(on: boolean): void {
    this.audible = on;
    if (on) {
      this.voice.unlock();
      // Give the browser a moment to start audio, then pick the line up.
      setTimeout(() => {
        if (this.audible && this.line) this.voice.say(this.line, CAPTION_RATE);
      }, 180);
    } else {
      this.voice.stop();
    }
  }

  get muted(): boolean {
    return this.voice.muted;
  }

  setMuted(muted: boolean): void {
    this.voice.muted = muted;
    try {
      localStorage.setItem(MUTED_KEY, muted ? "1" : "0");
    } catch { /* storage is a convenience */ }
    if (muted) this.voice.stop();
    else if (this.audible && this.line) this.voice.say(this.line, CAPTION_RATE);
  }

  dispose(): void {
    this.realm?.dispose();
    this.voice.dispose();
  }
}
